import { SegmentedControl } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { t } from "i18next";
import { useMemo } from "react";

import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { DetailsPanelComments } from "#modules/quoteRequestLines/views/DetailsPanel/DetailsPanelComments";
import { DetailsPanelCosts } from "#modules/quoteRequestLines/views/DetailsPanel/DetailsPanelCosts";
import { DetailsPanelFooter } from "#modules/quoteRequestLines/views/DetailsPanel/DetailsPanelFooter";
import { DetailsPanelHeader } from "#modules/quoteRequestLines/views/DetailsPanel/DetailsPanelHeader";
import { ProcessStepForTable } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStepsTable";
import { useAssembliesContext } from "#modules/quoteRequests/context/AssembliesContext";
import { Drawer } from "#shared/view/components/Drawer";
import { Stack } from "#shared/view/components/Stack";
import { AssemblyWithChildren } from "shared";

import { DetailsPanelAssemblySummary } from "./DetailsPanelAssemblySummary";

type Props = {
  onClosePanel: () => void;
  part: PartDto;
  quoteRequestLine: QuoteRequestLineDto;
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  assemblyChildren: AssemblyWithChildren<QuoteRequestLineDto>[];
  onAssemblyActivatedForBatch?: (quoteRequestLineId: string) => void;
  hasChildren: boolean;
  isChild: boolean;
  processSteps: ProcessStepForTable[];
};

export const DetailsPanelContent = ({
  onClosePanel,
  part,
  quoteRequestLine,
  assembliesInBatch,
  assemblyChildren,
  onAssemblyActivatedForBatch,
  hasChildren,
  isChild,
  processSteps,
}: Props) => {
  const [shouldDisplayComment, { open: onDisplayComment }] = useDisclosure(false);

  const { setActiveAssemblyForBatch } = useAssembliesContext();

  const isBatch = assembliesInBatch.length > 1;

  const displayCommentPanel = Boolean(
    shouldDisplayComment ||
    (quoteRequestLine.comment && quoteRequestLine.comment.length > 0) ||
    (quoteRequestLine.internalComment && quoteRequestLine.internalComment.length > 0),
  );

  const segmentedControlData = useMemo(
    () =>
      assembliesInBatch.map((assembly) => ({
        label: `${t("qrl.quantity")} ${assembly.quoteRequestLine.quantity}`,
        value: assembly.quoteRequestLine.id,
      })),
    [assembliesInBatch],
  );

  const setActiveQuoteRequestLine = (quoteRequestLineId: string) => {
    setActiveAssemblyForBatch(part.id, quoteRequestLineId);

    onAssemblyActivatedForBatch?.(quoteRequestLineId);
  };

  return (
    <Drawer.Content>
      <Stack h={"100%"} p="xs" gap="xs" justify="space-between" flex="1">
        <Stack gap="sm">
          <DetailsPanelHeader
            part={part}
            quoteRequestLine={quoteRequestLine}
            isCommentVisible={displayCommentPanel}
            onDisplayComment={onDisplayComment}
            onClosePanel={onClosePanel}
            hasChildren={hasChildren}
          />
          {isBatch && (
            <SegmentedControl
              data={segmentedControlData}
              value={quoteRequestLine.id}
              onChange={setActiveQuoteRequestLine}
            />
          )}
          {hasChildren && <DetailsPanelAssemblySummary quoteRequestLineId={quoteRequestLine.id} />}
          <DetailsPanelCosts
            title={
              hasChildren
                ? t("quoteRequestLine.allChildrenCost")
                : t("quoteRequestLine.costPerPiece")
            }
            hasChildren={hasChildren}
            isChild={isChild}
            quoteRequestLine={quoteRequestLine}
            assemblyChildren={assemblyChildren}
            processSteps={processSteps}
          />
          {displayCommentPanel && <DetailsPanelComments quoteRequestLineId={quoteRequestLine.id} />}
        </Stack>

        <Stack gap="xs">
          <DetailsPanelFooter quoteRequestLineId={quoteRequestLine.id} hasChildren={hasChildren} />
        </Stack>
      </Stack>
    </Drawer.Content>
  );
};
